import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { sendWhatsAppOTP, verifyWhatsAppOTP } from '@/lib/firebase/auth/whatsapp-phone-auth';

type SubMode = 'phone' | 'code';

interface VerifyPanelProps {
  onReady: (api: VerifyApi) => void;
  onVerified: () => void;
}

export interface VerifyApi {
  subMode: SubMode;
  primaryLabel: string;
  primaryDisabled: boolean;
  primaryBusy: boolean;
  primaryAction: () => void;
  backLabel: string | null;
  backAction: (() => void) | null;
}

export function VerifyPanel({ onReady, onVerified }: VerifyPanelProps) {
  const { t, language } = useLanguage();
  const [subMode, setSubMode] = useState<SubMode>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSendCode = async () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      toast.error(t('landing.requestTreatment.sendError'));
      return;
    }
    setBusy(true);
    try {
      const res = await sendWhatsAppOTP(trimmed, language as 'en' | 'ar', 'request-treatment');
      if (res.success && res.verificationId) {
        setVerificationId(res.verificationId);
        setSubMode('code');
      } else {
        toast.error(res.error || t('landing.requestTreatment.sendError'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('landing.requestTreatment.sendError'));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationId || !code.trim()) {
      toast.error(t('landing.requestTreatment.verifyError'));
      return;
    }
    setBusy(true);
    try {
      const res = await verifyWhatsAppOTP(verificationId, code.trim());
      if (!res.success) {
        toast.error(res.error || t('landing.requestTreatment.verifyError'));
        return;
      }
      if (res.authSessionEstablished === false) {
        toast.error(t('landing.requestTreatment.verifyError'));
        return;
      }
      onVerified();
    } catch (err: any) {
      toast.error(err?.message || t('landing.requestTreatment.verifyError'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    onReady({
      subMode,
      primaryLabel:
        subMode === 'phone'
          ? t('landing.requestTreatment.sendCode')
          : t('landing.requestTreatment.verify'),
      primaryDisabled: subMode === 'phone' ? !phone.trim() : !code.trim(),
      primaryBusy: busy,
      primaryAction: subMode === 'phone' ? handleSendCode : handleVerifyCode,
      backLabel:
        subMode === 'code' ? t('landing.requestTreatment.changePhone') : null,
      backAction:
        subMode === 'code'
          ? () => {
              setSubMode('phone');
              setCode('');
              setVerificationId(null);
            }
          : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subMode, phone, code, busy]);

  return (
    <div className="space-y-4">
      {subMode === 'phone' ? (
        <div className="space-y-2">
          <Label htmlFor="rt-phone">
            {t('landing.requestTreatment.phoneLabel')}
          </Label>
          <Input
            id="rt-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('landing.requestTreatment.phonePlaceholder')}
            dir="ltr"
            disabled={busy}
          />
          {busy && (
            <div className="flex items-center text-xs text-muted-foreground gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('landing.requestTreatment.sendCode')}…
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="rt-code">
            {t('landing.requestTreatment.codeLabel')}
          </Label>
          <Input
            id="rt-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('landing.requestTreatment.codePlaceholder')}
            dir="ltr"
            inputMode="numeric"
            disabled={busy}
          />
          <button
            type="button"
            className="text-xs text-[#7dda55] hover:underline"
            onClick={handleSendCode}
            disabled={busy}
          >
            {t('landing.requestTreatment.resend')}
          </button>
        </div>
      )}
    </div>
  );
}
